import api from './api';
import { ApiResponse } from '../types';

export interface FormulaDto {
  type: 'SUM' | 'AVERAGE' | 'WEIGHTED_SUM';
  questions: QuestionWeightDto[];
}

export interface QuestionWeightDto {
  questionId: number;
  weight: number;
  /** 矩阵题只取这些行(1-based, 按题内行定义顺序)参与计分；不传/空=整题 */
  matrixRows?: number[];
}

// 条件配置类型
export type ConditionType = 'SCORE_RANGE' | 'SCORE_EQUALS' | 'TEXT_EQUALS' | 'EXPRESSION';

// 分数区间条件
export interface ScoreRangeCondition {
  type: 'SCORE_RANGE';
  min: number | null;
  max: number | null;
  minInclusive: boolean;
  maxInclusive: boolean;
}

// 分数等于条件
export interface ScoreEqualsCondition {
  type: 'SCORE_EQUALS';
  value: number;
}

// 文本等于条件
export interface TextEqualsCondition {
  type: 'TEXT_EQUALS';
  questionId: number;
  expectedText: string;
}

// 表达式条件
export interface ExpressionCondition {
  type: 'EXPRESSION';
  expression: string;
}

export type ConditionConfig = ScoreRangeCondition | ScoreEqualsCondition | TextEqualsCondition | ExpressionCondition;

export interface ConclusionRuleDto {
  conditionType: ConditionType;
  conditionConfig: ConditionConfig;
  conclusionTitle: string;
  conclusionDescription?: string;
  recommendations?: string;
  level: string;
  orderNum: number;
  crisisAlert?: boolean;
}

export interface CreateIndicatorRequest {
  name: string;
  description?: string;
  orderNum: number;
  formula: FormulaDto;
  conclusionRules: ConclusionRuleDto[];
  enabled?: boolean;
}

export interface IndicatorResponse {
  id: number;
  questionnaireId: number;
  name: string;
  description?: string;
  orderNum: number;
  formula: string;
  enabled: boolean;
  conclusionRules: ConclusionRuleResponse[];
}

export interface ConclusionRuleResponse {
  id: number;
  conditionType: string;
  conditionValue: string; // JSON string
  conclusionTitle: string;
  conclusionDescription?: string;
  recommendations?: string;
  level: string;
  orderNum: number;
  crisisAlert?: boolean;
}

export const indicatorService = {
  /**
   * Create a new indicator
   */
  createIndicator: async (
    questionnaireId: number,
    request: CreateIndicatorRequest
  ): Promise<IndicatorResponse> => {
    const response = await api.post<ApiResponse<IndicatorResponse>>(
      `/admin/questionnaires/${questionnaireId}/indicators`,
      request
    );
    return response.data.data;
  },

  /**
   * Get all indicators for a questionnaire
   */
  getIndicators: async (questionnaireId: number): Promise<IndicatorResponse[]> => {
    const response = await api.get<ApiResponse<IndicatorResponse[]>>(
      `/admin/questionnaires/${questionnaireId}/indicators`
    );
    return response.data.data;
  },

  /**
   * Get indicator by ID
   */
  getIndicator: async (indicatorId: number): Promise<IndicatorResponse> => {
    const response = await api.get<ApiResponse<IndicatorResponse>>(
      `/admin/indicators/${indicatorId}`
    );
    return response.data.data;
  },

  /**
   * Update an indicator
   */
  updateIndicator: async (
    indicatorId: number,
    request: CreateIndicatorRequest
  ): Promise<IndicatorResponse> => {
    const response = await api.put<ApiResponse<IndicatorResponse>>(
      `/admin/indicators/${indicatorId}`,
      request
    );
    return response.data.data;
  },

  /**
   * Delete an indicator
   */
  deleteIndicator: async (indicatorId: number): Promise<void> => {
    await api.delete(`/admin/indicators/${indicatorId}`);
  },
};
